import { useMemo, useState } from "react";

/**
 * Image sources for responsive srcset. Match the names of the sizes
 * defined in orbrasil-cms Media collection:
 *   thumbnail (300×300) + thumbnail_2x (600×600)
 *   card (600×400)      + card_2x (1200×800)
 *   hero (1200×800)     + hero_2x (2400×1600)
 *   hero_square (640×640) + hero_square_2x (1280×1280)
 *
 * AVIF variants (`_avif` suffix) parallel the WebP set 1:1. When
 * present, FadeImage emits a `<source type="image/avif">` ahead of
 * the WebP source so AVIF-capable browsers (Chrome 85+, Safari 16+,
 * Firefox 93+) win the ~25-30% bandwidth saving. When ABSENT (old
 * media uploaded pre-AVIF config), the AVIF source is omitted
 * entirely — browser falls through to the WebP source. No 404, no
 * waste.
 *
 * Any subset is fine — missing entries just aren't advertised to the
 * browser. The widest present size is used as the `src` fallback for
 * browsers that ignore srcset.
 */
export interface ImageSources {
  // WebP set (universal fallback — every modern browser supports it).
  thumbnail?: string;
  thumbnail_2x?: string;
  card?: string;
  card_2x?: string;
  hero?: string;
  hero_2x?: string;
  /**
   * 1:1 aspect crops centred on the editor's focal point. Used by the
   * round hero containers on ProductLine / ProductFamily / ProductGroup /
   * ServicePage so the image isn't double-cropped (once by Sharp into
   * a 3:2 hero, again by CSS object-cover into a circle). Optional —
   * falls back to `hero` when absent.
   */
  hero_square?: string;
  hero_square_2x?: string;
  // AVIF set — only populated for media uploaded after CMS Media
  // imageSizes added the AVIF variants. Old media stays WebP-only.
  thumbnail_avif?: string;
  thumbnail_2x_avif?: string;
  card_avif?: string;
  card_2x_avif?: string;
  hero_avif?: string;
  hero_2x_avif?: string;
  hero_square_avif?: string;
  hero_square_2x_avif?: string;
}

interface FadeImageProps {
  /** Primary image source. Used as the <img> fallback inside <picture>. */
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  /**
   * Browser fetch priority hint. Set to "high" for above-the-fold
   * hero images so the browser starts the network request before
   * laying out the rest of the document. Default "auto" lets the
   * browser decide. Pairs well with loading="eager" — the two
   * attributes do different things (loading=lazy/eager controls
   * deferral, fetchpriority controls queue ordering).
   */
  fetchPriority?: "high" | "low" | "auto";
  /**
   * Optional WebP source. When provided, the component renders a
   * <picture> element with WebP as the preferred source and `src` as
   * the fallback <img>. Browsers that don't support WebP (or can't
   * fetch it) display the `src` instead — zero broken images (L17).
   */
  webpSrc?: string;
  /**
   * Optional responsive size variants (typically from CMS Media
   * sizes). When provided, FadeImage emits a srcset + sizes attribute
   * so the browser fetches only the variant that matches the current
   * viewport × DPR — 30–70% bandwidth win on mobile and non-hero
   * slots without manual per-screen work.
   */
  sources?: ImageSources;
  /**
   * `sizes` attribute that tells the browser how wide the image will
   * be displayed at various viewports. Default matches the common
   * responsive pattern: full-width on mobile, constrained on desktop.
   * Override when the image is displayed in a known narrower slot
   * (e.g. "300px" for a thumbnail, "(min-width: 1024px) 600px, 100vw"
   * for a card).
   */
  sizes?: string;
  /**
   * Square-hero mode for the round accent on line/family/group/service
   * pages. The image fills a fixed 1:1 box, so we emit **density**
   * (1x/2x) srcsets from the `hero_square*` variants — including AVIF —
   * instead of width descriptors. Density needs no `sizes` attribute
   * (the browser picks purely by devicePixelRatio), which is exactly
   * right for a fixed box and sidesteps the width/`sizes` guesswork a
   * variable-width responsive image needs. Requires `sources` carrying
   * `hero_square` / `hero_square_2x` (+ `_avif`); falls back to a plain
   * `<img src>` when those are absent.
   */
  squareOnly?: boolean;
  /**
   * `object-fit` for the rendered <img>. Default "cover" (fill + crop —
   * right for hero/card slots). Pass "contain" for slots that must show
   * the WHOLE uploaded image without cropping — e.g. the product
   * gallery's main highlight, where images vary in aspect ratio.
   */
  objectFit?: "cover" | "contain";
  /**
   * Editor-defined tile framing (`media.tile_crop`): the region of the image
   * the tile should show, as percentages of the original {x, y, width,
   * height}. Set in the CMS by dragging + zooming the image inside a 4:3
   * window (TileFramingField).
   *
   * When present it REPLACES `object-fit`: the <img> is scaled so the crop
   * region exactly fills the container and offset so the region's top-left
   * lands at the container's top-left. Both are pure CSS percentages, so the
   * result is resolution-independent and matches the CMS preview exactly
   * (same formula on both sides).
   *
   *   width  = 100 / (crop.width/100)  -> the crop occupies the full container
   *   translate(-x%, -y%)              -> % of the IMG's own box, i.e. the
   *                                       crop's origin in image space
   *
   * `height: auto` keeps the natural aspect, so the image is never distorted
   * even if the stored rect drifts slightly off 4:3. Requires a positioned,
   * overflow-hidden parent — every tile container already is.
   */
  crop?: { x: number; y: number; width: number; height: number } | null;
}

const DEFAULT_SIZES =
  "(min-width: 1280px) 1200px, (min-width: 768px) 50vw, 100vw";

/**
 * Build a `srcset` string from an ImageSources map. Each entry gets
 * a width descriptor so the browser can pick based on viewport width.
 *
 * `variant` selects the format family: "webp" uses the WebP set,
 * "avif" uses the parallel `_avif` set. Pulling each format into its
 * own srcset is critical — mixing AVIF + WebP URLs inside a single
 * srcset breaks the format-negotiation: browsers can't reject by
 * format from a single srcset, only the `<source type=...>` mime
 * gates it. So we emit two `<source>` siblings, each with its own
 * srcset.
 */
function buildSrcSet(
  sources: ImageSources,
  variant: "webp" | "avif" = "webp",
): string {
  const entries: Array<[number, string]> = [];
  const pick = (
    webp: string | undefined,
    avif: string | undefined,
  ): string | undefined => (variant === "avif" ? avif : webp);

  const t = pick(sources.thumbnail, sources.thumbnail_avif);
  const t2 = pick(sources.thumbnail_2x, sources.thumbnail_2x_avif);
  const c = pick(sources.card, sources.card_avif);
  const c2 = pick(sources.card_2x, sources.card_2x_avif);
  const h = pick(sources.hero, sources.hero_avif);
  const h2 = pick(sources.hero_2x, sources.hero_2x_avif);

  if (t) entries.push([300, t]);
  if (t2) entries.push([600, t2]);
  if (c) entries.push([600, c]);
  if (c2) entries.push([1200, c2]);
  if (h) entries.push([1200, h]);
  if (h2) entries.push([2400, h2]);

  // Deduplicate (card + hero both advertise 1200w — pick one) and sort.
  const seen = new Set<number>();
  return entries
    .filter(([w]) => {
      if (seen.has(w)) return false;
      seen.add(w);
      return true;
    })
    .sort(([a], [b]) => a - b)
    .map(([w, url]) => `${url} ${w}w`)
    .join(", ");
}

/**
 * Cheap probe: does this sources map have ANY AVIF variant? Used to
 * decide whether the `<source type="image/avif">` line is worth
 * emitting at all. When false, we skip the AVIF source and the
 * picture element matches the pre-AVIF behavior exactly.
 */
function hasAvif(sources: ImageSources | undefined): boolean {
  if (!sources) return false;
  return Boolean(
    sources.thumbnail_avif ||
      sources.thumbnail_2x_avif ||
      sources.card_avif ||
      sources.card_2x_avif ||
      sources.hero_avif ||
      sources.hero_2x_avif ||
      sources.hero_square_avif ||
      sources.hero_square_2x_avif,
  );
}

/**
 * Density-descriptor srcset ("url 1x, url 2x") for fixed-size images.
 * Unlike width descriptors it needs no `sizes` attribute — the browser
 * picks purely by devicePixelRatio, which is exactly right for the fixed
 * round hero box. Omits absent variants.
 */
function densitySet(
  x1: string | undefined,
  x2: string | undefined,
): string | undefined {
  const parts = [x1 ? `${x1} 1x` : null, x2 ? `${x2} 2x` : null].filter(
    Boolean,
  ) as string[];
  return parts.length ? parts.join(", ") : undefined;
}

/**
 * Image with neutral fallback. No opacity transitions, no colorful
 * placeholder gradient — relies on the parent's background while
 * loading. This avoids the visible "blink" when a bright placeholder
 * gets replaced by an actual image.
 *
 * Responsive srcset (sources prop) + WebP picture fallback (webpSrc
 * prop) compose cleanly — you can use either, both, or neither.
 */
const FadeImage = ({
  src,
  alt,
  className = "",
  width,
  height,
  loading = "lazy",
  fetchPriority = "auto",
  webpSrc,
  sources,
  sizes = DEFAULT_SIZES,
  squareOnly = false,
  objectFit = "cover",
  crop,
}: FadeImageProps) => {
  const [error, setError] = useState(false);
  // Square hero: fixed 1:1 box → density (1x/2x) srcsets, no `sizes`.
  const isSquare = Boolean(squareOnly && sources);

  // Memoize srcset strings — `sources` is a plain object so reference
  // equality is rarely stable across renders; recomputing short
  // strings each render is fine but this keeps it minimal.
  const srcSet = useMemo(
    () =>
      isSquare
        ? densitySet(sources!.hero_square, sources!.hero_square_2x)
        : sources
          ? buildSrcSet(sources, "webp")
          : undefined,
    [sources, isSquare],
  );
  const avifSrcSet = useMemo(
    () =>
      isSquare
        ? densitySet(sources!.hero_square_avif, sources!.hero_square_2x_avif)
        : hasAvif(sources)
          ? buildSrcSet(sources!, "avif")
          : undefined,
    [sources, isSquare],
  );
  // Density mode (square) selects by DPR alone — emit no `sizes`.
  const effectiveSizes = isSquare ? undefined : srcSet ? sizes : undefined;

  if (error) {
    return (
      <div className="w-full h-full bg-muted flex items-center justify-center">
        <div className="w-12 h-12 rounded-lg bg-muted-foreground/10" />
      </div>
    );
  }

  // A valid framing rect must have a positive width; anything else falls back
  // to plain object-fit so a malformed value can never blank out a tile.
  const useCrop = !!crop && crop.width > 0 && crop.height > 0;
  // Keep float noise (5.79 + 42.3 = 48.089999999999996) out of the emitted CSS.
  const round4 = (n: number) => Math.round(n * 1e4) / 1e4;
  const cropStyle: React.CSSProperties | undefined = useCrop
    ? {
        position: "absolute",
        // Offsets go on left/top, NOT transform. The tiles zoom on hover via
        // Tailwind's `group-hover:scale-110`, which is itself a `transform` —
        // and an inline transform beats a stylesheet one, so putting the crop
        // there silently killed the hover zoom on framed tiles only.
        //
        // These are equivalent to the old translate: left/top percentages
        // resolve against the CONTAINER, and the container spans exactly
        // crop.width% × crop.height% of the image, so the crop's origin lands
        // at the container's origin either way. Verified identical on every
        // real crop, zoomed in and out.
        left: `${(-crop!.x / crop!.width) * 100}%`,
        top: `${(-crop!.y / crop!.height) * 100}%`,
        width: `${100 / (crop!.width / 100)}%`,
        height: "auto",
        maxWidth: "none",
        // Pivot the hover zoom on the middle of the VISIBLE region rather than
        // the middle of the (larger, off-centre) image, so a framed tile zooms
        // exactly like an unframed one instead of drifting off-subject.
        transformOrigin: `${round4(crop!.x + crop!.width / 2)}% ${round4(crop!.y + crop!.height / 2)}%`,
      }
    : undefined;

  const img = (
    <img
      src={src}
      srcSet={srcSet}
      sizes={effectiveSizes}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      // React types lowercase fetchpriority since v19; older types
      // expect camelCase. Cast lets us write the standard attribute
      // without a runtime any.
      {...({ fetchpriority: fetchPriority } as Record<string, string>)}
      decoding="async"
      className={
        useCrop ? className : `w-full h-full object-${objectFit} ${className}`
      }
      style={cropStyle}
      onError={() => setError(true)}
    />
  );

  // No alternate sources at all → emit plain <img>.
  if (!webpSrc && !avifSrcSet) return img;

  return (
    // `contents` removes the <picture> box from layout so the <img> below
    // becomes the direct child of the caller's container. Without it the
    // inline <picture> shrink-wraps and the img's `w-full h-full` resolves
    // against the picture (not the container) — which letterboxed images
    // in fixed-size slots like the comparador tiles.
    <picture className="contents">
      {/* AVIF first — browsers pick the first <source> whose type they
          support. AVIF-capable clients short-circuit here; older
          browsers move on to the WebP source. Skipped entirely when
          no AVIF variants are present (old media). */}
      {avifSrcSet && (
        <source
          type="image/avif"
          srcSet={avifSrcSet}
          sizes={effectiveSizes}
        />
      )}
      {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
      {img}
    </picture>
  );
};

export default FadeImage;
